import { useMemo } from 'react';
import { AutocompleteOption } from 'redesign';
import { useParams } from 'react-router-dom';
import { RenderUnsettledUI } from 'shared/components';
import { CertificateWithFileUrl } from 'shared/components/forms/accreditation-form/accreditation-form';
import { getWorkerFullName } from 'shared/utils/worker';
import { WorkerList } from 'hq/services/flex-worker-service.types';
import { useGetCertificates } from './hooks/use-get-certificates';
import { EmployeeCertificateAddPage } from './employee-certificate-add-page';
import { useGetEmployeeDetail } from '../../hooks/use-get-employee-detail';

export const EmployeeCertificateAddPageDataProvider = () => {
  const { id } = useParams<'id'>();
  const employeeQuery = useGetEmployeeDetail(id ?? '');
  const certificatesQuery = useGetCertificates();

  const flexWorkerOption = useMemo<AutocompleteOption | null>(() => {
    if (employeeQuery.status !== 'success' || !employeeQuery.data) {
      return null;
    }
    const employee = employeeQuery.data;
    const fullName = getWorkerFullName({
      firstName: employee.firstName,
      infix: employee.infix,
      lastName: employee.lastName,
    });
    return {
      label: `${fullName} (${employee.email})`,
      value: employee.id,
    };
  }, [employeeQuery]);

  if (employeeQuery.status !== 'success') {
    return <RenderUnsettledUI data={employeeQuery} />;
  }

  if (certificatesQuery.status !== 'success') {
    return <RenderUnsettledUI data={certificatesQuery} />;
  }

  const employee = employeeQuery.data;
  const flexWorkers: WorkerList[] = [
    {
      workerId: employee.id,
      email: employee.email,
      firstName: employee.firstName ?? '',
      infix: employee.infix ?? '',
      lastName: employee.lastName ?? '',
      employmentType: null,
    },
  ];

  const certificates: CertificateWithFileUrl[] = (certificatesQuery.data || []).map((cert) => ({
    id: cert.id,
    name: cert.name,
    canHaveReceipt: cert.canHaveReceipt,
    fileUrl: cert.fileUrl,
  }));

  return (
    <EmployeeCertificateAddPage
      certificates={certificates}
      flexWorkers={flexWorkers}
      flexWorkerOption={flexWorkerOption}
    />
  );
};
